import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const baseUrl = process.env.REACT_APP_API || '';
const enabled = String(process.env.REACT_APP_UNME_FALLBACK_ENABLED || '') === '1';
const noop = () => {};
const isAuthFailure = (error) => Number(error?.response?.data?.code) === 9999 || error?.response?.status === 401;

// 기능 정보는 화면에 들어올 때마다 조회한다. 조회 실패를 미지원으로 처리하지 않는다.
export function useContractBreakdownAvailability(onAuthError = noop) {
  const [state, setState] = useState({ status: enabled ? 'loading' : 'disabled' });
  const [requestKey, setRequestKey] = useState(0);
  const retry = useCallback(() => setRequestKey((key) => key + 1), []);
  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    setState({ status: 'loading' });
    axios.get(`${baseUrl}/api/cp/payment/manual/config`, { withCredentials: true })
      .then(({ data }) => {
        if (!active) return;
        if (Number(data?.code) === 9999) { onAuthError(); return; }
        if (data?.code !== 1000 || typeof data?.data?.contractBreakdownAvailable !== 'boolean') {
          throw new Error('기능 설정을 확인할 수 없습니다.');
        }
        setState({ status: data.data.contractBreakdownAvailable ? 'available' : 'disabled' });
      })
      .catch((error) => {
        if (!active) return;
        if (isAuthFailure(error)) { onAuthError(); return; }
        setState({ status: 'error' });
      });
    return () => { active = false; };
  }, [requestKey, onAuthError]);
  return { ...state, retry };
}

export function BreakdownToggle({ expanded, onClick, label }) {
  return (
    <button type="button" aria-label={`${label} ${expanded ? '간단히 보기' : '자세히 보기'}`}
      aria-expanded={expanded} onClick={onClick}
      className="shrink-0 rounded-md border border-[var(--brand-primary)] bg-white px-2 py-1 text-[12px] font-bold text-[var(--brand-primary)] hover:bg-[var(--brand-primary-soft)]">
      {expanded ? '간단히 보기 ▲' : '자세히 보기 ▼'}
    </button>
  );
}

const formatAmount = (value) => {
  if (value === null || value === undefined || value === '') return '확인 필요';
  if (typeof value === 'number' && !Number.isSafeInteger(value)) return '확인 필요';
  const amount = String(value).trim();
  if (!/^-?\d+$/.test(amount)) return '확인 필요';
  try { return `${window.BigInt(amount).toLocaleString('ko-KR')}원`; } catch (_) { return '확인 필요'; }
};

const contractHeaderClass = 'whitespace-nowrap px-2 py-1.5 text-left text-[12px] font-semibold text-[#6b7684]';
const numberHeaderClass = 'whitespace-nowrap px-2 py-1.5 text-right text-[12px] font-semibold text-[#6b7684]';
const contractCellClass = 'whitespace-nowrap px-2 py-1.5 text-left text-[13px] font-bold text-[#333d4b]';
const numberCellClass = 'whitespace-nowrap px-2 py-1.5 text-right text-[13px] font-semibold tabular-nums text-[#191f28]';
const isUnmappedContract = (row) => [row?.contractId, row?.contract_id, row?.contractKey, row?.contract_key]
  .some((value) => typeof value === 'string' && value.trim().toUpperCase() === 'UNMAPPED');
const contractName = (row) => row?.contractId === null || row?.contract_id === null || row?.contractKey === null || row?.contract_key === null ? '-' : row?.contractName || '-';

export function ContractBreakdown({ period, periodKey, onAuthError = noop }) {
  const [state, setState] = useState({ status: 'loading' });
  const [requestKey, setRequestKey] = useState(0);
  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    const params = period === 'monthly' ? { month: periodKey } : { date: periodKey };
    axios.get(`${baseUrl}/api/cp/settlement/contracts/${period}`, { params, withCredentials: true })
      .then(({ data }) => {
        if (!active) return;
        if (Number(data?.code) === 9999) { onAuthError(); return; }
        if (data?.code !== 1000 || !data?.data || !Array.isArray(data.data.rows)) throw new Error('조회 실패');
        if (data.data.supported !== true || data.data.contractBreakdownAvailable !== true) {
          setState({ status: 'unsupported' });
          return;
        }
        setState({ status: 'ready', result: data.data });
      })
      .catch((error) => {
        if (!active) return;
        if (isAuthFailure(error)) { onAuthError(); return; }
        setState({ status: 'error' });
      });
    return () => { active = false; };
  }, [period, periodKey, requestKey, onAuthError]);

  if (state.status === 'loading') return <div className="py-2 text-[12px] text-[#6b7684]" role="status">상세 내역을 불러오는 중입니다.</div>;
  if (state.status === 'unsupported') return <div className="py-2 text-[12px] text-[#6b7684]">상세 내역을 이용할 수 없습니다.</div>;
  if (state.status === 'error') return <div className="py-2 text-[12px] text-[#b45309]" role="alert">상세 내역을 불러오지 못했습니다. <button type="button" className="underline font-bold" onClick={() => setRequestKey((key) => key + 1)}>다시 조회</button></div>;

  const result = state.result;
  const monthly = period === 'monthly';
  const rows = result.rows.filter((row) => !isUnmappedContract(row));
  const unmapped = result.rows.filter(isUnmappedContract);
  return (
    <div className="w-full text-[12px] text-[#374151]">
      <div className="overflow-x-auto">
        {monthly ? <table className="w-full min-w-[480px] table-auto border-collapse">
          <thead className="border-b border-[#dfe5eb] bg-[#f8fafc]"><tr><th scope="col" className={contractHeaderClass}>구분</th><th scope="col" className={numberHeaderClass}>순매출</th><th scope="col" className={numberHeaderClass}>집계 정산</th><th scope="col" className={numberHeaderClass}>지급 성공</th></tr></thead>
          <tbody>{rows.map((row, index) => <tr key={row.contractId ?? index} className="border-b border-[#eef1f4] last:border-0">
            <th scope="row" className={contractCellClass}>{contractName(row)}</th>
            <td className={numberCellClass}>{formatAmount(row.netSalesAmount)}</td><td className={numberCellClass}>{formatAmount(row.settlementAmount)}</td><td className={numberCellClass}>{formatAmount(row.paidAmount)}</td>
          </tr>)}</tbody>
        </table> : <table className="w-full min-w-[720px] table-auto border-collapse">
          <thead className="border-b border-[#dfe5eb] bg-[#f8fafc]"><tr>
            <th scope="col" className={contractHeaderClass}>구분</th>
            <th scope="col" className={numberHeaderClass}>승인 / 취소</th><th scope="col" className={numberHeaderClass}>승인금액</th><th scope="col" className={numberHeaderClass}>취소금액</th><th scope="col" className={numberHeaderClass}>순수수료</th><th scope="col" className={numberHeaderClass}>예상 정산액</th><th scope="col" className={numberHeaderClass}>집계 정산액</th>
          </tr></thead>
          <tbody>{rows.map((row, index) => <tr key={row.contractId ?? index} className="border-b border-[#dfe5eb] last:border-0">
            <th scope="row" className={contractCellClass}>{contractName(row)}</th>
            <td className={numberCellClass}>{row.approvalCount ?? '—'}건 / {row.cancellationCount ?? '—'}건</td><td className={numberCellClass}>{formatAmount(row.approvedAmount)}</td><td className={numberCellClass}>{formatAmount(row.cancelAmount)}</td><td className={numberCellClass}>{Number(row.missingFeeCount) > 0 ? `미확정 (확인된 금액 ${formatAmount(row.netFeeAmount)})` : formatAmount(row.netFeeAmount)}</td><td className={numberCellClass}>{formatAmount(row.estimatedNetAmount)}</td><td className={numberCellClass}>{formatAmount(row.settlementAmount)}</td>
          </tr>)}</tbody>
        </table>}
        {result.rows.length === 0 && <div>조회된 상세 내역이 없습니다.</div>}
      </div>
      {unmapped.length > 0 && <details className="mt-2 text-[#b45309]"><summary className="cursor-pointer font-semibold">일부 내역을 확인할 수 없습니다.</summary>{unmapped.map((row, index) => <p key={index} className="mt-1 text-[#4e5968]">{monthly ? `순매출 ${formatAmount(row.netSalesAmount)} · 집계 정산 ${formatAmount(row.settlementAmount)} · 지급 성공 ${formatAmount(row.paidAmount)}` : `승인 ${row.approvalCount ?? '—'}건 · 취소 ${row.cancellationCount ?? '—'}건 · 승인금액 ${formatAmount(row.approvedAmount)} · 취소금액 ${formatAmount(row.cancelAmount)} · 순수수료 ${Number(row.missingFeeCount) > 0 ? `미확정 (확인된 금액 ${formatAmount(row.netFeeAmount)})` : formatAmount(row.netFeeAmount)} · 예상 정산액 ${formatAmount(row.estimatedNetAmount)} · 집계 정산액 ${formatAmount(row.settlementAmount)}`}</p>)}</details>}
      <p className="mt-1 text-[11px] leading-4 text-[#6b7684]">{result.complete === false ? '일부 내역 확인 필요 · ' : ''}전체 합계에는 다른 결제 내역이 포함될 수 있습니다.{monthly ? ' 지급 성공액은 서버 확인일 기준입니다.' : ' 예상 정산액은 참고값이며 집계 정산액과 다를 수 있습니다.'}</p>
    </div>
  );
}
